/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityAffiliatesRule.module.scss';

const EDGES = [
  { d: 'M290 104 V144 H470 V184', len: 398, pct: '15%', px: 306, py: 128 },
  { d: 'M830 104 V144 H650 V184', len: 398, pct: '35%', px: 778, py: 128 },
  { d: 'M560 292 V366', len: 74, pct: '50%', px: 576, py: 334 },
];

const NODE_EDGES = [[0], [1], [0, 1, 2], [2]];

type Translate = (key: string) => string;

const getCards = (t: Translate) => [
  {
    tag: t('Aggregation'),
    text: t(
      'Holdings by separate listed parties add together. Neither owner needs a controlling stake, and neither needs to appear in the same programme.',
    ),
  },
  {
    tag: t('Reset at each level'),
    text: t(
      'Once an entity is caught, its own 50% holdings are caught in turn. A clean tier-one supplier can sit above a restricted tier three.',
    ),
  },
  {
    tag: t('Red Flag 29'),
    text: t(
      'Being unable to determine an ownership percentage is itself a red flag under EAR Know Your Customer guidance, to be resolved before the export proceeds.',
    ),
  },
];

const STEPS: { nodes?: number[]; edges?: number[] }[] = [
  { nodes: [0, 1] },
  { edges: [0, 1] },
  { nodes: [2] },
  { edges: [2] },
  { nodes: [3] },
];

const STEP_START_MS = 180;
const STEP_MS = 400;

export const DefenceSecurityAffiliatesRule = () => {
  const { t } = useInt();
  const cards = getCards(t);
  const diagramRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(diagramRef, { threshold: 0.14 });
  const [nodesOn, setNodesOn] = useState([false, false, false, false]);
  const [edgesOn, setEdgesOn] = useState([false, false, false]);
  const [playKey, setPlayKey] = useState(0);
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    const timers = STEPS.map((step, i) =>
      setTimeout(
        () => {
          if (step.nodes) {
            setNodesOn((prev) => {
              const next = [...prev];
              step.nodes?.forEach((n) => {
                next[n] = true;
              });
              return next;
            });
          }

          if (step.edges) {
            setEdgesOn((prev) => {
              const next = [...prev];
              step.edges?.forEach((e) => {
                next[e] = true;
              });
              return next;
            });
          }
        },
        STEP_START_MS + i * STEP_MS,
      ),
    );

    return () => timers.forEach(clearTimeout);
  }, [revealed, playKey]);

  const replay = () => {
    setNodesOn([false, false, false, false]);
    setEdgesOn([false, false, false]);
    setPlayKey((key) => key + 1);
  };

  return (
    <Section background="#F7F9FD" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Two listed parties. Neither controls it. The licence is still required.')}
          description={t(
            'Under the Affiliates Rule, holdings by listed parties aggregate. A supplier named on nothing, held 15% by one listed party and 35% by another, carries the same licence requirement as its owners. And the calculation resets at every level below it.',
          )}
        />
      </div>

      <div className={styles.diagramWrap} ref={diagramRef}>
        <svg
          className={styles.graph}
          viewBox="0 0 1120 512"
          role="img"
          aria-label="Sample export control ownership cascade. A party on the Entity List holds 15 percent of an unlisted supplier and a party on the Military End User List holds 35 percent. Together they hold 50 percent in aggregate, so the supplier carries the same licence requirement. The supplier in turn holds 50 percent of a downstream subsidiary, which is also caught because the calculation resets at each level."
        >
          <defs>
            <linearGradient id="dsarGradListed" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff6e8" />
              <stop offset="100%" stopColor="#fcefd9" />
            </linearGradient>
            <linearGradient id="dsarGradSupplier" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff9ef" />
              <stop offset="100%" stopColor="#fdf4e6" />
            </linearGradient>
            <linearGradient id="dsarGradDown" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fbfcff" />
              <stop offset="100%" stopColor="#f6f8fd" />
            </linearGradient>
            <filter id="dsarShadow" x="-30%" y="-30%" width="160%" height="180%">
              <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#0a0d14" floodOpacity="0.1" />
            </filter>
            <filter id="dsarShadowHover" x="-30%" y="-30%" width="160%" height="180%">
              <feDropShadow dx="0" dy="10" stdDeviation="14" floodColor="#0a0d14" floodOpacity="0.16" />
            </filter>
          </defs>

          {EDGES.map((edge, i) => (
            <path
              key={edge.d}
              className={cn(
                styles.edge,
                edgesOn[i] && styles.on,
                hoveredNode !== null && NODE_EDGES[hoveredNode].includes(i) && styles.hot,
              )}
              style={{ '--len': edge.len } as CSSProperties}
              d={edge.d}
            />
          ))}
          {EDGES.map((edge, i) => (
            <g
              key={`${edge.pct}-${edge.px}`}
              className={cn(hoveredNode !== null && NODE_EDGES[hoveredNode].includes(i) && styles.hot)}
            >
              <rect className={styles.pctBg} x={edge.px - 8} y={edge.py - 16} width="52" height="22" rx="6" />
              <text className={styles.pct} x={edge.px} y={edge.py}>
                {edge.pct}
              </text>
            </g>
          ))}

          <g transform="translate(140,8)">
            <g
              className={cn(styles.node, nodesOn[0] && styles.on, hoveredNode === 0 && styles.hovered)}
              onMouseEnter={() => setHoveredNode(0)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <title>Listed party A &middot; BIS Entity List</title>
              <rect
                className={cn(styles.box, styles.boxListed)}
                width="300"
                height="96"
                rx="14"
                fill="url(#dsarGradListed)"
              />
              <circle className={styles.dotHalo} cx="20" cy="25" r="7" />
              <circle className={cn(styles.dot, styles.dotAmber)} cx="20" cy="25" r="3.5" />
              <text className={cn(styles.lab, styles.labAmber)} x="33" y="30">
                ENTITY LIST
              </text>
              <text className={styles.name} x="20" y="58">
                Listed party A
              </text>
              <text className={styles.src} x="20" y="80">
                BIS Entity List &middot; holds 15% of the supplier
              </text>
            </g>
          </g>

          <g transform="translate(680,8)">
            <g
              className={cn(styles.node, nodesOn[1] && styles.on, hoveredNode === 1 && styles.hovered)}
              onMouseEnter={() => setHoveredNode(1)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <title>Listed party B &middot; BIS Military End User List</title>
              <rect
                className={cn(styles.box, styles.boxListed)}
                width="300"
                height="96"
                rx="14"
                fill="url(#dsarGradListed)"
              />
              <circle className={styles.dotHalo} cx="20" cy="25" r="7" />
              <circle className={cn(styles.dot, styles.dotAmber)} cx="20" cy="25" r="3.5" />
              <text className={cn(styles.lab, styles.labAmber)} x="33" y="30">
                MILITARY END USER LIST
              </text>
              <text className={styles.name} x="20" y="58">
                Listed party B
              </text>
              <text className={styles.src} x="20" y="80">
                BIS MEU List &middot; holds 35% of the supplier
              </text>
            </g>
          </g>

          <g transform="translate(380,184)">
            <g
              className={cn(styles.node, nodesOn[2] && styles.on, hoveredNode === 2 && styles.hovered)}
              onMouseEnter={() => setHoveredNode(2)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <title>Supplier C &middot; unlisted, licence required</title>
              <rect
                className={cn(styles.box, styles.boxSupplier)}
                width="360"
                height="108"
                rx="16"
                fill="url(#dsarGradSupplier)"
              />
              <circle className={styles.dotHalo} cx="20" cy="25" r="7" />
              <circle className={cn(styles.dot, styles.dotAccent)} cx="20" cy="25" r="3.5" />
              <text className={styles.lab} x="33" y="30">
                UNLISTED SUPPLIER
              </text>
              <rect className={styles.badgeWarn} x="228" y="17" width="114" height="22" rx="11" />
              <text className={styles.badgeWarnText} x="285" y="32" textAnchor="middle">
                LICENCE REQUIRED
              </text>
              <text className={styles.name} x="20" y="62" style={{ fontSize: 17 }}>
                Supplier C
              </text>
              <text className={styles.src} x="20" y="84">
                Named on no list &middot; registry filings resolve the holdings
              </text>
              <text className={styles.note} x="340" y="84" textAnchor="end">
                50% in aggregate
              </text>
            </g>
          </g>

          <g transform="translate(380,366)">
            <g
              className={cn(styles.node, nodesOn[3] && styles.on, hoveredNode === 3 && styles.hovered)}
              onMouseEnter={() => setHoveredNode(3)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <title>Subsidiary D &middot; also caught, calculation resets here</title>
              <rect
                className={cn(styles.box, styles.boxDown)}
                width="360"
                height="100"
                rx="16"
                fill="url(#dsarGradDown)"
              />
              <circle className={styles.dotHalo} cx="20" cy="25" r="7" />
              <circle className={cn(styles.dot, styles.dotMuted)} cx="20" cy="25" r="3.5" />
              <text className={styles.lab} x="33" y="30">
                DOWNSTREAM SUBSIDIARY
              </text>
              <rect className={styles.badgeMuted} x="252" y="17" width="90" height="22" rx="11" />
              <text className={styles.badgeMutedText} x="297" y="32" textAnchor="middle">
                ALSO CAUGHT
              </text>
              <text className={styles.name} x="20" y="62" style={{ fontSize: 17 }}>
                Subsidiary D
              </text>
              <text className={styles.src} x="20" y="84">
                Held 50% by Supplier C &middot; the calculation resets here
              </text>
            </g>
          </g>

          <text className={styles.caption} x="560" y="500" textAnchor="middle">
            Under the rule of most restrictiveness, the strictest requirement of any owner governs, whatever the size of
            its stake.
          </text>
        </svg>

        <button type="button" className={styles.replay} onClick={replay}>
          {t('Replay cascade')}
        </button>
      </div>

      <div className={styles.cards}>
        {cards.map((card) => (
          <div className={styles.card} key={card.tag}>
            <span className={styles.cardTag}>{card.tag}</span>
            <p className={styles.cardText}>{card.text}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};
